import { type AnyElement, toKebab } from "@zyzgroup/core-common";
import { VNode } from "../vnode";
import { scheduleVnodeAsap, updateVnode } from "./useVnode";
import { atomReadStyles, setStyle } from "./useStyle";
import { YZCanvasContext } from "./useDraw/canvas";

/// attribute 直接改变 HTML 的属性

// 对外接口
export function useAttribute(
  el: AnyElement,
  prop?: string | Record<string, any>,
  value?: string
) {
  /// read all
  if (prop === undefined && value === undefined) {
    return atomReadAttributes(el);
  }
  /// read single
  if (typeof prop == "string" && value === undefined) {
    return atomReadAttribute(el, prop);
  }
  // let obj: Record<string, string> = {};
  if (typeof prop == "object") {
    /// write all
    // obj = prop;
    updateVnode(el, (vnode: VNode) => {
      for (const k in prop) {
        if (k == "style") {
          setStyle(el, k, prop[k]);
        } else {
          if (Object.prototype.hasOwnProperty.call(prop, k)) {
            vnode.props[k] = "" + prop[k];
          }
        }
      }
    });
  } else if (typeof prop == "string" && typeof value == "string") {
    /// write single
    // obj[prop] = "" + value;
    if (prop == "style") {
      setStyle(el, prop, value);
    } else {
      updateVnode(el, (vnode: VNode) => {
        vnode.props[prop] = "" + value;
      });
    }
  }
  /// 性能原因，el.setAttribute() 耗时久, 改为使用虚拟DOM
  // Object.entries(obj).forEach(([prop, val]) => {
  //   el.setAttribute(toKebab(prop), val);
  // });
  scheduleVnodeAsap();
}

export function atomReadAttributes(el: AnyElement) {
  const obj: Record<string, string | Record<string, string>> = {};
  for (let i = 0, attrs = el.attributes, n = attrs.length; i < n; i++) {
    const attr = attrs[i];
    if (attr.nodeName && attr.nodeValue) {
      if (attr.nodeName === "style") {
        obj[attr.nodeName] = atomReadStyles(el, true);
      } else {
        obj[attr.nodeName] = attr.nodeValue;
      }
    }
  }
  return obj;
}

export function atomReadAttribute(el: AnyElement, prop: string) {
  if (prop === "style") {
    return atomReadStyles(el, true);
  } else {
    return el.getAttribute(toKebab(prop));
  }
}

export function atomWriteAttribute(
  el: AnyElement,
  prop: string,
  value: string
) {
  return el.setAttribute(toKebab(prop), "" + value);
}

export function atomRemoveAttribute(el: AnyElement, attrs: string) {
  attrs.split(/[,\s]+/i).forEach((attr) => {
    const a = toKebab(attr.trim());
    if (a != "") {
      el.removeAttribute(a);
    }
  });
}

// convert css or svg attrs to canvas attrs
export function cssAttrsToCanvasAttrs(
  ctx: YZCanvasContext,
  attrs: Record<string, string | CanvasGradient>
) {
  // "fill-opacity": number;
  // "opacity": number;
  // "letter-spacing": number;
  for (const key in attrs) {
    const value = attrs[key];
    switch (key) {
      case "fill":
        // fill: "none"
        if (value !== "none") {
          ctx.fillStyle = value;
        }
        break;
      case "stroke":
        // stroke: "none"
        if (value !== "none") {
          ctx.strokeStyle = value;
        }
        break;
      case "stroke-width":
      case "strokeWidth":
        ctx.lineWidth = Number(value);
        break;
      case "stroke-linecap":
      case "strokeLinecap":
        // 线段的端点样式
        // butt | round | square
        ctx.lineCap = value as CanvasLineCap;
        break;
      case "stroke-linejoin":
      case "strokeLinejoin":
        switch (value) {
          case "round":
          case "bevel":
          case "miter":
            ctx.lineJoin = value as CanvasLineJoin;
            break;
          default: // arcs | miter-clip
            ctx.lineJoin = "miter";
            break;
        }
        break;
      case "stroke-miterlimit":
      case "strokeMiterlimit":
        // 角重合长度, 角重合长度超过这个比例的部分会被cut
        ctx.miterLimit = Number(value);
        break;
      case "stroke-opacity":
      case "strokeOpacity":
        break;
      case "stroke-dashoffset":
      case "strokeDashoffset":
        ctx.lineDashOffset = Number(value);
        break;
      case "stroke-dasharray":
      case "strokeDasharray":
        // stroke-dasharray: 100 100;
        if (typeof value === "string") {
          ctx.setLineDash(value.split(/\s+/).map((s) => Number(s)));
        }
        break;
      case "cursor":
        break;
      case "font":
        if (typeof value === "string") {
          ctx.font = value;
        }
        break;
      case "font-size":
      case "fontSize":
        ctx.font += ` ${value}`;
        break;
      case "font-family":
      case "fontFamily":
        ctx.font += ` ${value}`;
        break;
      case "font-weight":
      case "fontWeight":
        break;
      case "font-style":
      case "fontStyle":
        break;
      case "text-anchor":
      case "textAnchor":
        switch (value) {
          case "middle":
            ctx.textAlign = "center";
            break;
          case "end":
            // ctx.textAlign = "right";
            ctx.textAlign = "end";
            break;
          case "start":
            // ctx.textAlign = "left";
            ctx.textAlign = "start";
            break;
        }
        break;
      case "alignment-baseline":
      case "alignmentBaseline":
        switch (value) {
          case "middle":
          case "bottom":
          case "top":
          case "alphabetic":
          case "hanging":
          case "ideographic":
            ctx.textBaseline = value;
            break;
          default: // auto | baseline | before-edge | text-before-edge  | central | after-edge | text-after-edge  | mathematical | center |
            ctx.textBaseline = "alphabetic";
            break;
        }
        break;
      /// UNFINISH
    }
  }
}
